import { IconButton, Tooltip } from "@material-ui/core";
import {
    createStyles,
    StyledComponentProps,
    WithStyles,
    withStyles,
} from "@material-ui/core/styles";
import * as React from "react";

import { TextInput, RelodeTheme } from "../";
import { PlusIcon, TrashIcon } from "../../assets/icons";
import { AttachmentLink } from "./attachmentLink";

const styles = (theme: RelodeTheme) =>
    createStyles({
        root: {
            marginBottom: theme.spacing(4),
        },
        inputRow: {
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
            marginBottom: theme.spacing(4),
            [theme.breakpoints.down("md")]: {
                alignItems: "flex-start",
                marginBottom: theme.spacing(1),
            }
        },
        input: {
            flex: 1,
            margin: theme.spacing(0, 1, 0, 0),
        },
        errorMessage: {
            position: "absolute",
            top: "100%",
        },
        linkRow: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: theme.spacing(1),
        },
    });

interface OwnProps {
    id?: string;
    label?: string;
    numberOfLinks?: number;
    links: string[];
    handleChange: (links: string[]) => void;
    error?: boolean;
    helperText?: string;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

export interface LinkCollectionProps
    extends OwnProps,
        StyledComponentProps<any> {}

interface State {
    newUrl: string;
    error?: string;
}

export const LinkCollection = withStyles(styles)(
    class extends React.Component<Props, State> {
        constructor(props: Props) {
            super(props);

            this.state = {
                newUrl: "",
            };
        }

        handleTextChange = (e: React.ChangeEvent<any>) => {
            const { value } = e.target;
            this.setState({ newUrl: value, error: undefined });
        };

        handleAdd = () => {
            const { newUrl } = this.state;

            const { links = [], numberOfLinks = 5 } = this.props;

            const tmpLinks = [...links];

            if (!newUrl) {
                this.setState({
                    error: "url is required",
                });
                return;
            }

            const url_pattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/;

            if (!url_pattern.test(newUrl)) {
                this.setState({
                    error: "url is not valid",
                });
                return;
            }
            if (tmpLinks.length < numberOfLinks) {
                tmpLinks.push(newUrl);

                this.setState(
                    {
                        newUrl: "",
                        error: undefined,
                    },
                    () => this.props.handleChange(tmpLinks)
                );
            }
        };

        handleRemove = (i: number) => {
            const { links } = this.props;
            const tmpLinks = [...links];
            tmpLinks.splice(i, 1);
            this.props.handleChange(tmpLinks);
        };

        render() {
            const {
                id,
                classes,
                links = [],
                numberOfLinks = 5,
                label = `add links (up to ${this.props.numberOfLinks})`,

                error = !!this.state.error,
                helperText,
            } = this.props;

            const errorMessage = this.state.error || helperText;

            return (
                <div className={classes.root} id={`${id}LinkCollectionWrapper`}>
                    <div className={classes.inputRow}>
                        <TextInput
                            data-cy="linkCollection-link-input"
                            disabled={links.length >= numberOfLinks}
                            className={classes.input}
                            label={label}
                            id="link"
                            name="link"
                            value={this.state.newUrl}
                            error={error || !!errorMessage}
                            FormHelperTextProps={{
                                classes: {
                                    root: classes.errorMessage,
                                },
                            }}
                            helperText={errorMessage}
                            onChange={this.handleTextChange}
                            onEnterKey={this.handleAdd}
                        />
                        <Tooltip placement="bottom-start" title="add link">
                            <IconButton
                                data-cy="linkCollection-link-addButton"
                                id={`${id}LinkCollectionAddButton`}
                                disabled={links.length >= numberOfLinks}
                                color="primary"
                                onClick={this.handleAdd}
                            >
                                <PlusIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                    {links &&
                        links.map((link: string, i: number) => (
                            <div className={classes.linkRow} key={i}>
                                <AttachmentLink
                                    baseId={`${id}LinkCollection${i}`}
                                    url={link}
                                    label={link}
                                    icon
                                    type="web"
                                    highlightType="external"
                                />
                                <Tooltip
                                    placement="bottom-start"
                                    title={`remove ${link}`}
                                >
                                    <IconButton
                                        id={`${id}LinkCollectionRemoveLink${i}Button`}
                                        color="primary"
                                        onClick={() => this.handleRemove(i)}
                                    >
                                        <TrashIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        ))}
                </div>
            );
        }
    }
);
